/**
 * Reusable multi-select searchable combobox for filters.
 */

import { useState, useMemo, useEffect } from 'react';
import { useDebounce } from '../hooks/useDebounce';
import { X, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectComboboxProps {
  options: MultiSelectOption[];
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  /** Plural name for display (e.g., "positions", "campaigns") */
  displayName?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  className?: string;
  /** Width of the popover content */
  popoverWidth?: string;
  /** Callback when search query changes (for server-side search) */
  onSearchChange?: (query: string) => void;
  /** Controlled search value (for server-side search) */
  searchValue?: string;
}

export function MultiSelectCombobox({
  options,
  values,
  onChange,
  placeholder,
  displayName,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  isLoading = false,
  className,
  popoverWidth = 'w-[240px]',
  onSearchChange,
  searchValue,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');
  const debouncedSearch = useDebounce(internalSearch, 300);

  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(debouncedSearch);
    }
  }, [debouncedSearch, onSearchChange]);

  const toggleValue = (value: string) => {
    if (values.includes(value)) {
      onChange(values.filter((v) => v !== value));
    } else {
      onChange([...values, value]);
    }
    // Don't close popover - let user select multiple
  };

  const clearAll = () => {
    onChange([]);
    setOpen(false);
  };

  const handleSearchChange = (query: string) => {
    setInternalSearch(query);
  };

  // Use controlled search value if provided, otherwise use internal state
  const currentSearchValue = searchValue !== undefined ? searchValue : internalSearch;

  // Display text logic: 0 = placeholder, 1 = name, >1 = "N [type] selected"
  const displayText = useMemo(() => {
    if (values.length === 0) return placeholder;
    if (values.length === 1) {
      const selected = options.find((opt) => opt.value === values[0]);
      return selected?.label || placeholder;
    }
    return `${values.length} ${displayName || 'items'} selected`;
  }, [values, options, placeholder, displayName]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'justify-between bg-white font-normal',
            values.length > 0 && 'border-orange-500 bg-orange-50',
            className
          )}
        >
          <span className="truncate">{displayText}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(popoverWidth, 'p-0')}
        align="start"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={currentSearchValue}
            onValueChange={handleSearchChange}
          />
          <CommandList>
            <CommandEmpty>{isLoading ? 'Loading...' : emptyMessage}</CommandEmpty>
            <CommandGroup>
              {/* Clear All option - only show when items are selected */}
              {values.length > 0 && (
                <CommandItem onSelect={clearAll} className="text-red-600">
                  <X className="mr-2 h-4 w-4" />
                  Clear all ({values.length})
                </CommandItem>
              )}
              {options.map((option) => {
                const isSelected = values.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggleValue(option.value)}
                  >
                    <Checkbox checked={isSelected} className="mr-2 h-4 w-4" />
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
